
import { useState } from "react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TablesInsert } from "@/integrations/supabase/types";
import { calculateBusinessDays, getCurrentYearDateRange } from "@/utils/dateUtils";
import { DateRangeSelector } from "./vacation/DateRangeSelector";
import { VacationSummary } from "./vacation/VacationSummary";
import { toast as sonnerToast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

type TeamScope = "sales" | "production";

const VACATION_REASONS = ["Annual Leave", "Personal Time Off", "Family Event", "Medical Appointment", "Other"] as const;

export function VacationRequestForm() {
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>();
  const [reason, setReason] = useState<string>("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ["user_profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      const { data, error } = await supabase.from("profiles").select("*").eq("id", user.id).single();
      if (error) throw error;
      return data;
    }
  });

  const { data: approvedVacations } = useQuery({
    queryKey: ["approved_vacations", profile?.id, new Date().getFullYear()],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { start, end } = getCurrentYearDateRange();
      const { data, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("user_id", profile.id)
        .eq("status", "approved")
        .gte("start_date", start)
        .lte("start_date", end);
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id
  });

  const { data: busyPeriods } = useQuery({
    queryKey: ["busy_periods", profile?.role],
    queryFn: async () => {
      if (!profile?.role) return [];
      const teamScope: TeamScope = profile.role === "sales" ? "sales" : "production";
      const { data, error } = await supabase.from("busy_periods").select("*").eq("team_scope", teamScope);
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.role
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!selectedDate?.from || !selectedDate?.to) {
      toast({ title: "Please select dates", variant: "destructive" });
      return;
    }
    if (!reason) {
      toast({ title: "Please select a reason", variant: "destructive" });
      return;
    }

    try {
      setIsSubmitting(true);
      sonnerToast.loading("Submitting vacation request...");
      
      const businessDays = calculateBusinessDays(selectedDate.from, selectedDate.to);
      const requestedDays = isHalfDay ? businessDays * 0.5 : businessDays;
      const usedVacationDays = approvedVacations?.reduce((total, vacation) => {
        const start = new Date(vacation.start_date);
        const end = new Date(vacation.end_date);
        const days = calculateBusinessDays(start, end);
        return total + (vacation.is_half_day ? days * 0.5 : days);
      }, 0) || 0;

      if (requestedDays > (profile?.vacation_days || 0) - usedVacationDays) {
        sonnerToast.dismiss();
        toast({
          title: "Not enough vacation days",
          description: `You only have ${(profile?.vacation_days || 0) - usedVacationDays} days available`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        sonnerToast.dismiss();
        toast({ title: "Authentication error", description: "You're not logged in.", variant: "destructive" });
        setIsSubmitting(false);
        return;
      }
      
      const startDate = new Date(selectedDate.from);
      startDate.setHours(12, 0, 0, 0);
      const endDate = new Date(selectedDate.to);
      endDate.setHours(12, 0, 0, 0);
      
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const newRequest: TablesInsert<"vacation_requests"> = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        reason,
        is_half_day: isHalfDay,
        user_id: user.id
      };
      
      const { data: vacationRequest, error } = await supabase.from("vacation_requests").insert(newRequest).select().single();
      
      if (error) {
        sonnerToast.dismiss();
        toast({ title: "Database error", description: "Failed to save your vacation request.", variant: "destructive" });
        setIsSubmitting(false);
        return;
      }
      
      if (vacationRequest) {
        try {
          const notificationData = {
            employeeName: profile?.full_name || user.email,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            reason,
            isHalfDay,
            requestId: vacationRequest.id
          };
          
          const notificationResponse = await supabase.functions.invoke('send-vacation-notification', { body: notificationData });
          sonnerToast.dismiss();
          if (notificationResponse.error) {
            toast({ title: "Request submitted", description: "Saved, but notification email failed.", variant: "default" });
          } else {
            sonnerToast.success("Vacation request submitted successfully!");
            toast({ title: "Success", description: "Vacation request submitted and notifications sent" });
          }
        } catch {
          sonnerToast.dismiss();
          toast({ title: "Request submitted", description: "Saved, but notification issue.", variant: "default" });
        }
      }
      
      setSelectedDate(undefined);
      setReason("");
      setIsHalfDay(false);
      queryClient.invalidateQueries({ queryKey: ["vacation_requests"] });
      navigate("/profile");
    } catch {
      sonnerToast.dismiss();
      toast({ title: "Error", description: "Failed to submit vacation request", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      {/* Header: title left, controls right */}
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4 space-y-0">
        <div>
          <CardTitle>
            Request Vacation
            <span className="block text-sm text-muted-foreground font-normal">
              Select your dates and submit a vacation request
            </span>
          </CardTitle>
        </div>

        <div className="flex items-center gap-3 flex-wrap">

          <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-background">
            <Label htmlFor="half-day" className="font-medium text-sm whitespace-nowrap">Half day</Label>
            <Switch id="half-day" checked={isHalfDay} onCheckedChange={setIsHalfDay} />
          </div>

          <div className="min-w-[180px]">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Reason for leave" />
              </SelectTrigger>
              <SelectContent>
                {VACATION_REASONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="transition-all hover:scale-[1.02]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Request"
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary row */}
        <VacationSummary
          selectedDate={selectedDate}
          isHalfDay={isHalfDay}
          profile={profile}
          approvedVacations={approvedVacations}
        />

        {/* Full-width Calendar */}
        <DateRangeSelector
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          busyPeriods={busyPeriods}
          fullWidth
        />

        {/* Legend row */}
        <div className="flex items-center gap-4 flex-wrap border rounded-lg px-4 py-3 bg-muted/30">
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-destructive"></div>
            <span className="text-muted-foreground">Production Busy</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-[#9b87f5]"></div>
            <span className="text-muted-foreground">Sales Busy</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-muted"></div>
            <span className="text-muted-foreground">Weekend</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-primary"></div>
            <span className="text-muted-foreground">Selected</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <div className="w-3 h-3 rounded-full bg-accent"></div>
            <span className="text-muted-foreground">Today</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
